import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Facebook, X, Check, ExternalLink, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { DEPARTMENTS, DEFAULT_EVENT_LABELS } from "./galleryConstants";

const inputCls = "w-full px-4 py-2.5 border border-gray-200 rounded-sm focus:outline-none focus:border-[#003B5C] bg-white";
const labelCls = "block text-xs font-bold uppercase tracking-wider text-[#1E1E1E]/60 mb-2";

const SetupNotice = ({ status }) => (
  <div data-testid="facebook-setup-notice" className="bg-[#FFF8E6] border border-[#D4A437]/50 rounded-sm p-5 text-sm text-[#1E1E1E]/85 space-y-3">
    <div className="flex items-center gap-2 font-bold text-[#003B5C]"><AlertTriangle size={16} className="text-[#D4A437]" /> Not connected — setup needed</div>
    <p>Importing photos from the church Facebook Page uses the Meta Graph API. It works automatically once these values are added to the backend environment (never paste them in chat):</p>
    <ul className="list-disc pl-5 space-y-1">
      <li><code className="bg-white px-1 rounded">FACEBOOK_PAGE_ID</code> — the numeric ID of the official church Page{status?.missing?.includes("FACEBOOK_PAGE_ID") ? "" : " (set)"}</li>
      <li><code className="bg-white px-1 rounded">FACEBOOK_ACCESS_TOKEN</code> — a long-lived <strong>Page Access Token</strong>{status?.missing?.includes("FACEBOOK_ACCESS_TOKEN") ? "" : " (set)"}</li>
    </ul>
    <p className="font-semibold text-[#003B5C]">How to obtain them</p>
    <ol className="list-decimal pl-5 space-y-1">
      <li>Create a Meta App at <a className="underline" href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer">developers.facebook.com/apps</a> (Business type) and add the <em>Facebook Login</em> product.</li>
      <li>In Graph API Explorer, generate a User token with <code>pages_show_list</code>, <code>pages_read_engagement</code> and <code>pages_read_user_content</code>, then exchange it for a long-lived token and request the Page token via <code>/me/accounts</code>.</li>
      <li>Public use of these permissions requires Meta App Review; while the app is in Development mode the token works for Page admins/testers only.</li>
      <li>Add both values to the production environment variables and redeploy.</li>
    </ol>
    <p>Until then, keep using Upload / Image URL / Google Drive, and visitors can still open the official Facebook page from the Gallery.</p>
  </div>
);

export const FacebookImport = ({ onClose, onImported, eventTitles, existingLabels }) => {
  const [status, setStatus] = useState(null);
  const [albums, setAlbums] = useState([]);
  const [album, setAlbum] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [selected, setSelected] = useState({});
  const [meta, setMeta] = useState({ department: "General Church", event_label: "", published: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/gallery/status").then((r) => {
      setStatus(r.data);
      if (r.data.connected) api.get("/gallery/albums").then((a) => setAlbums(a.data.albums || []));
    }).catch(() => setStatus({ connected: false, missing: ["FACEBOOK_PAGE_ID", "FACEBOOK_ACCESS_TOKEN"] }));
  }, []);

  const openAlbum = async (a) => {
    setAlbum(a);
    setPhotos([]);
    try { const r = await api.get(`/gallery/albums/${a.id}/photos`); setPhotos(r.data.photos || []); }
    catch { toast.error("Couldn't load album photos"); }
  };

  const toggle = (p) => setSelected((s) => { const n = { ...s }; if (n[p.id]) delete n[p.id]; else n[p.id] = p; return n; });
  const selectedList = Object.values(selected);

  const doImport = async () => {
    if (selectedList.length === 0) return toast.error("Select at least one photo");
    setBusy(true);
    try {
      const r = await api.post("/gallery/facebook/import", {
        photos: selectedList.map((p) => ({ fb_id: p.id, source: p.full, caption: "", photo_date: (p.created_time || "").slice(0, 10) })),
        ...meta,
      });
      toast.success(`Imported ${r.data.imported} photo${r.data.imported === 1 ? "" : "s"}${r.data.failed?.length ? ` · ${r.data.failed.length} skipped` : ""}`);
      onImported();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Import failed");
    } finally { setBusy(false); }
  };

  const eventOptions = [...new Set([...(eventTitles || []), ...(existingLabels || []), ...DEFAULT_EVENT_LABELS].filter(Boolean))];

  return (
    <div data-testid="facebook-import-modal" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center z-10">
          <h3 className="font-display text-xl font-bold text-[#003B5C] inline-flex items-center gap-2"><Facebook size={18} /> Import from Facebook</h3>
          <button type="button" onClick={onClose} data-testid="facebook-import-close" className="p-2 hover:bg-gray-100 rounded-sm"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-5">
          {!status ? <div className="text-sm text-[#1E1E1E]/60">Checking connection…</div> : !status.connected ? (
            <>
              <SetupNotice status={status} />
              {status.facebook_url && (
                <a href={status.facebook_url} target="_blank" rel="noopener noreferrer" data-testid="facebook-import-page-link" className="inline-flex items-center gap-2 text-sm font-semibold text-[#003B5C] underline">
                  Open the official Facebook page <ExternalLink size={14} />
                </a>
              )}
            </>
          ) : (
            <>
              <div className="text-xs text-[#1E1E1E]/60">Connected to Page {status.page_id_hint}. Only selected photos are imported, and nothing is published unless you choose to.</div>
              {!album ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {albums.length === 0 && <div className="col-span-full text-sm text-[#1E1E1E]/60">No albums found.</div>}
                  {albums.map((a) => (
                    <button key={a.id} type="button" onClick={() => openAlbum(a)} data-testid={`facebook-album-${a.id}`} className="border border-gray-200 hover:border-[#003B5C] rounded-sm overflow-hidden text-left">
                      <div className="aspect-video bg-[#F7F8FA]">{a.cover && <img src={a.cover} alt="" className="w-full h-full object-cover" />}</div>
                      <div className="p-2 text-xs"><div className="font-semibold text-[#003B5C] truncate">{a.name}</div><div className="text-[#1E1E1E]/50">{a.count} photos</div></div>
                    </button>
                  ))}
                </div>
              ) : (
                <>
                  <button type="button" onClick={() => { setAlbum(null); setPhotos([]); }} className="text-xs text-[#003B5C] underline">← All albums</button>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {photos.map((p) => (
                      <button key={p.id} type="button" onClick={() => toggle(p)} data-testid={`facebook-photo-${p.id}`} className={`relative aspect-square rounded-sm overflow-hidden border-2 ${selected[p.id] ? "border-[#D4A437]" : "border-transparent"}`}>
                        <img src={p.thumb} alt="" className="w-full h-full object-cover" />
                        {selected[p.id] && <span className="absolute top-1 right-1 bg-[#D4A437] text-white rounded-sm p-0.5"><Check size={12} /></span>}
                      </button>
                    ))}
                  </div>
                </>
              )}
              {selectedList.length > 0 && (
                <div className="border-t border-gray-100 pt-5 space-y-4">
                  <div className={labelCls}>Preview — {selectedList.length} selected</div>
                  <div className="flex gap-2 flex-wrap">{selectedList.map((p) => <img key={p.id} src={p.thumb} alt="" className="w-16 h-16 object-cover rounded-sm" />)}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div><label className={labelCls}>Department</label>
                      <select value={meta.department} onChange={(e) => setMeta({ ...meta, department: e.target.value })} className={inputCls}>{DEPARTMENTS.map((d) => <option key={d}>{d}</option>)}</select></div>
                    <div><label className={labelCls}>Event / Category</label>
                      <select value={meta.event_label} onChange={(e) => setMeta({ ...meta, event_label: e.target.value })} className={inputCls}><option value="">— No event —</option>{eventOptions.map((t) => <option key={t}>{t}</option>)}</select></div>
                    <div><label className={labelCls}>Visibility</label>
                      <label className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-sm cursor-pointer"><input type="checkbox" checked={meta.published} onChange={(e) => setMeta({ ...meta, published: e.target.checked })} className="accent-[#003B5C]" /><span className="text-sm">Publish now</span></label></div>
                  </div>
                  <p className="text-xs text-[#1E1E1E]/55">Photos are copied into the website's own storage so they stay visible even if Facebook links expire. You can edit caption and date per photo afterwards.</p>
                  <div className="flex justify-end">
                    <button type="button" onClick={doImport} disabled={busy} data-testid="facebook-import-submit" className="bg-[#003B5C] text-white px-6 py-2.5 rounded-sm text-sm font-medium hover:bg-[#002A42] disabled:opacity-60">{busy ? "Importing…" : `Import ${selectedList.length} photo${selectedList.length === 1 ? "" : "s"}`}</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
